import { resolveFieldShortcuts, resolveSectionsFieldShortcuts } from '@folio/components';
import { useQuery } from '@tanstack/react-query';
import {
  configControllerGetPublicProductConfigQueryOptions,
  type ConfigControllerGetPublicProductConfigQueryResponse,
  type ConfigCustomFieldDto,
} from 'korint-sdk';
import type {
  Field,
  FieldShortcut,
  FormSections,
  FormSectionsWithShortcuts,
} from 'korint-sdk/react';
import { useMemo } from 'react';

export type FieldEntityType = 'customer' | 'policy' | 'asset';

type ProductConfig = ConfigControllerGetPublicProductConfigQueryResponse;

// Customer funnels have always edited the aggregate columns (name…) alongside the custom fields;
// policy and asset funnels only ever edited their custom ones.
const configByEntityType: Record<FieldEntityType, (data: ProductConfig) => ConfigCustomFieldDto[]> =
  {
    customer: data => [
      ...(data.customerFields ?? []),
      ...((data.customerAggregateFields ?? []) as ConfigCustomFieldDto[]),
    ],
    policy: data => data.policyFields ?? [],
    asset: data => data.assetFields ?? [],
  };

type EntityFieldsInput = {
  entityType: FieldEntityType;
  productId?: string;
};

/** The product's field config for one entity, for callers that resolve shortcuts themselves. */
export function useEntityFieldConfig({ entityType, productId }: EntityFieldsInput): {
  config: ConfigCustomFieldDto[];
  isLoading: boolean;
} {
  const { data: config, isLoading } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(productId!),
    enabled: Boolean(productId),
    select: configByEntityType[entityType],
  });

  return { config: config ?? [], isLoading };
}

/**
 * Drops the composites that only render — `vehicleInput` and its resolution gate — which are
 * neither columns, CSV headers nor payload keys.
 */
export function toPayloadFields(fields: Field[]): Field[] {
  return fields.filter(field => !field.ignoreInPayload);
}

export function useResolvedEntityFields({
  entityType,
  productId,
  fields: rawFields,
}: EntityFieldsInput & { fields: FieldShortcut[] }): {
  fields: Field[];
  payloadFields: Field[];
  config: ConfigCustomFieldDto[];
  isLoading: boolean;
} {
  const { config, isLoading } = useEntityFieldConfig({ entityType, productId });

  return useMemo(() => {
    const fields = resolveFieldShortcuts(rawFields, config);

    return {
      fields,
      payloadFields: toPayloadFields(fields),
      config,
      isLoading,
    };
  }, [rawFields, config, isLoading]);
}

export function useResolvedEntitySections({
  entityType,
  productId,
  sections: rawSections,
}: EntityFieldsInput & { sections: FormSectionsWithShortcuts }): {
  sections: FormSections;
  config: ConfigCustomFieldDto[];
  isLoading: boolean;
} {
  const { config, isLoading } = useEntityFieldConfig({ entityType, productId });

  return useMemo(
    () => ({ sections: resolveSectionsFieldShortcuts(rawSections, config), config, isLoading }),
    [rawSections, config, isLoading],
  );
}
